import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * QrDownloadPanel — renders the verification QR for a POS property and
 * lets design partners download it in the formats they actually need:
 *
 *   1. SVG (vector, transparent background) — primary. Drops cleanly
 *      into Canva, Illustrator, InDesign, Affinity. Scales to any size
 *      without pixelation. The right deliverable for any designer.
 *   2. PNG with transparent background (1024px) — for tools that don't
 *      accept SVG. The partner can put their own background behind it.
 *   3. PNG with white background (1024px) — safe default; usable
 *      anywhere with zero design thought.
 *
 * Every output bakes in a proper 4-module quiet zone on all four sides
 * so the codes are reliably scannable when embedded in any layout.
 *
 * Library: qrcode-generator (UMD, self-hosted at /lib/...). Chosen
 * because it exposes the QR matrix directly (getModuleCount + isDark),
 * letting us render canvas and SVG ourselves with consistent quiet-zone
 * handling. Smaller than qrcodejs and gives us SVG output for free.
 */

const QR_LIB_URL = '/lib/qrcode-generator.min.js';

interface QrInstance {
  getModuleCount(): number;
  isDark(row: number, col: number): boolean;
  addData(text: string): void;
  make(): void;
}

type QrFactory = (typeNumber: number, errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H') => QrInstance;

declare global {
  interface Window {
    qrcode?: QrFactory;
  }
}

// Quiet zone: 4 modules on every side is the QR spec minimum for
// reliable scanning. Lower than this and many phone cameras fail under
// poor lighting or odd angles.
const QUIET_ZONE_MODULES = 4;

let qrLoaderPromise: Promise<void> | null = null;

function loadQrLibrary(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.qrcode) return Promise.resolve();
  if (qrLoaderPromise) return qrLoaderPromise;

  qrLoaderPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = QR_LIB_URL;
    script.async = true;
    script.onload = () => {
      if (window.qrcode) resolve();
      else reject(new Error('QR library loaded but global not defined'));
    };
    script.onerror = () => reject(new Error('Could not load QR library'));
    document.head.appendChild(script);
  });
  return qrLoaderPromise;
}

function buildQr(text: string): QrInstance {
  const factory = window.qrcode;
  if (!factory) throw new Error('QR library not loaded');
  // typeNumber 0 = auto-detect smallest type that fits the data.
  const qr = factory(0, 'M');
  qr.addData(text);
  qr.make();
  return qr;
}

/**
 * Render the QR onto a canvas at the requested output size, with a
 * 4-module quiet zone and either a white or transparent background.
 *
 * The QR module grid is sized to fit inside the canvas after the
 * quiet zone is subtracted, so total output size matches the caller's
 * request exactly (256/1024/etc).
 */
function renderToCanvas(text: string, sizePx: number, opts: { transparent: boolean }): HTMLCanvasElement {
  const qr = buildQr(text);
  const modules = qr.getModuleCount();
  const totalModules = modules + QUIET_ZONE_MODULES * 2;
  // Floor to integer pixels so modules align on the pixel grid; pad
  // the leftover with extra quiet zone to keep size exactly sizePx.
  const modulePx = Math.floor(sizePx / totalModules);
  const renderedSize = modulePx * totalModules;
  const offset = Math.floor((sizePx - renderedSize) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');

  if (!opts.transparent) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sizePx, sizePx);
  }

  ctx.fillStyle = 'var(--color-sidebar)';
  for (let row = 0; row < modules; row++) {
    for (let col = 0; col < modules; col++) {
      if (qr.isDark(row, col)) {
        const x = offset + (QUIET_ZONE_MODULES + col) * modulePx;
        const y = offset + (QUIET_ZONE_MODULES + row) * modulePx;
        ctx.fillRect(x, y, modulePx, modulePx);
      }
    }
  }

  return canvas;
}

/**
 * Build a clean SVG string for the same QR — vector, transparent
 * background, 4-module quiet zone. ViewBox is sized to the module
 * grid so the receiving design tool can scale it to any size.
 */
function renderToSvg(text: string): string {
  const qr = buildQr(text);
  const modules = qr.getModuleCount();
  const totalModules = modules + QUIET_ZONE_MODULES * 2;

  // Use a single <path> with one move-then-rect per dark module — much
  // smaller file than emitting one <rect> per module, and renders
  // identically. Designers prefer compact SVGs.
  const rects: string[] = [];
  for (let row = 0; row < modules; row++) {
    for (let col = 0; col < modules; col++) {
      if (qr.isDark(row, col)) {
        const x = QUIET_ZONE_MODULES + col;
        const y = QUIET_ZONE_MODULES + row;
        rects.push(`M${x},${y}h1v1h-1z`);
      }
    }
  }

  const path = rects.join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalModules} ${totalModules}" shape-rendering="crispEdges">` +
    `<path fill="#1a1a1a" d="${path}"/>` +
    `</svg>`
  );
}

function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

function downloadCanvasAsPng(canvas: HTMLCanvasElement, filename: string): void {
  canvas.toBlob((blob) => {
    if (!blob) return;
    downloadBlob(blob, filename);
  }, 'image/png');
}

function downloadSvg(svgString: string, filename: string): void {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  downloadBlob(blob, filename);
}

interface QrDownloadPanelProps {
  /** The verification URL the QR encodes — full https://… */
  verifyUrl: string;
  /** Charter number — used in download filenames so the file is
   * identifiable on disk (e.g. POS-ZA-00001-qr.svg). */
  charterNumber: string;
}

/**
 * One-shot helper for surfaces that need a single "Download QR" button.
 * Generates a 1024px PNG (white background, universal) and triggers
 * the browser download. Loads the QR library on first call.
 *
 * Used by the Overview tab's Bedside certificate section, where the
 * three-format design-partner panel below is overkill.
 */
export async function downloadQrAsPng(verifyUrl: string, charterNumber: string): Promise<void> {
  await loadQrLibrary();
  const canvas = renderToCanvas(verifyUrl, 1024, { transparent: false });
  downloadCanvasAsPng(canvas, `${charterNumber}-qr-1024-white.png`);
}

function QrDownloadPanel({ verifyUrl, charterNumber }: QrDownloadPanelProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadQrLibrary();
        if (cancelled || !previewRef.current) return;
        // White background on preview so it reads at-a-glance against
        // any panel colour. Downloads still offer transparent and white
        // variants explicitly.
        const canvas = renderToCanvas(verifyUrl, 200, { transparent: false });
        canvas.style.display = 'block';
        canvas.style.borderRadius = '6px';
        previewRef.current.innerHTML = '';
        previewRef.current.appendChild(canvas);
        setReady(true);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'QR preview failed');
      }
    })();
    return () => { cancelled = true; };
  }, [verifyUrl]);

  const handleDownloadSvg = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await loadQrLibrary();
      const svg = renderToSvg(verifyUrl);
      downloadSvg(svg, `${charterNumber}-qr.svg`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBusy(false);
    }
  }, [verifyUrl, charterNumber, busy]);

  const handleDownloadPng = useCallback(async (sizePx: number, transparent: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await loadQrLibrary();
      const canvas = renderToCanvas(verifyUrl, sizePx, { transparent });
      const suffix = transparent ? 'transparent' : 'white';
      downloadCanvasAsPng(canvas, `${charterNumber}-qr-${sizePx}-${suffix}.png`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBusy(false);
    }
  }, [verifyUrl, charterNumber, busy]);

  const copyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
    } catch {
      // Clipboard blocked — operator can still select-and-copy manually.
    }
  }, [verifyUrl]);

  return (
    <div style={{
      border: '1px solid var(--color-border, #e1e5e9)',
      borderRadius: 'var(--radius)',
      padding: '1rem',
      background: 'var(--color-card-bg, #fff)',
      display: 'flex',
      gap: '1.25rem',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    }}>
      <div style={{
        flex: '0 0 auto',
        width: '200px',
        height: '200px',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border, #e1e5e9)',
        borderRadius: 'var(--radius)',
        position: 'relative',
      }}>
        <div
          ref={previewRef}
          style={{ width: '100%', height: '100%', borderRadius: 'var(--radius)', overflow: 'hidden' }}
        />
        {!ready && !error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--color-text-light)', fontSize: 'var(--text-md)',
            pointerEvents: 'none',
          }}>
            Preparing QR…
          </div>
        )}
        {error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--color-error-text)', fontSize: 'var(--text-md)', padding: '0.5rem',
            textAlign: 'center', pointerEvents: 'none',
          }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--text-lg)', marginBottom: '0.25rem' }}>
          QR Code — Download for Design Partners
        </div>
        <p style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)', margin: '0 0 0.875rem 0', lineHeight: 1.5 }}>
          Each download has a proper quiet zone built in &mdash; drops into any
          design tool ready to use, no cropping or padding needed.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.875rem' }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleDownloadSvg}
            disabled={!ready || busy}
            style={{ alignSelf: 'flex-start' }}
          >
            ⬇ Download SVG (vector — best for designers)
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handleDownloadPng(1024, true)}
            disabled={!ready || busy}
            style={{ alignSelf: 'flex-start' }}
          >
            ⬇ Download PNG · 1024px · transparent background
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handleDownloadPng(1024, false)}
            disabled={!ready || busy}
            style={{ alignSelf: 'flex-start' }}
          >
            ⬇ Download PNG · 1024px · white background
          </button>
        </div>

        <div style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--color-text-light)',
          padding: '0.5rem 0.625rem',
          background: 'var(--color-bg, #f9fafb)',
          border: '1px solid var(--color-border, #e1e5e9)',
          borderRadius: 'var(--radius-md)',
          wordBreak: 'break-all',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{verifyUrl}</span>
          <button
            type="button"
            className="btn btn-secondary btn-xs"
            style={{ fontSize: 'var(--text-xs)', padding: '0.25rem 0.5rem', flex: '0 0 auto' }}
            onClick={copyUrl}
          >
            Copy URL
          </button>
        </div>
      </div>
    </div>
  );
}

export default QrDownloadPanel;
